import _ from "lodash";
import makeCancellablePromise from "make-cancellable-promise";
import { CancellableAPIRequest, makeCancellableAPIRequest } from "restAPI";
import {
  AnyaCancelDto,
  AnyaConversationHistoryItem,
  AnyaConversationMessagesResponse,
  AnyaConversationRequestDto,
  AnyaEventDto,
  AnyaFeedbackDto,
  AnyaRequestDto,
  AnyaResponseDto,
  AnyaRetryDto,
} from "types/anya";
import { BaseResponseDto } from "types/api/baseResponseDto";
import { sha256Hash } from "views/Dashboards/anyaUtils";

export function getAnyaAssistant(data: AnyaRequestDto) {
  return makeCancellableAPIRequest<BaseResponseDto<AnyaResponseDto>>(
    "POST",
    "/api/ai/assistant",
    {
      data,
    },
  );
}

export function storeFeedback(
  conversationId: string,
  queryParams: AnyaFeedbackDto,
) {
  const queryParamsString = new URLSearchParams(
    _.mapValues(_.omitBy(queryParams, _.isNil), (value) => String(value)),
  );
  return makeCancellableAPIRequest<AnyaFeedbackDto>(
    "PUT",
    `/api/ai/assistant/conversations/${conversationId}/feedback?${queryParamsString}`,
  );
}

export function getAnyaConversations(params?: {
  excludeFindingRemediations?: boolean;
  widgetId?: string;
}) {
  return makeCancellableAPIRequest<
    BaseResponseDto<{ history: AnyaConversationHistoryItem[] }>
  >("GET", "/api/ai/assistant/conversations/list", {
    params,
  });
}

export function getAnyaConversationMessages(
  params: AnyaConversationRequestDto,
) {
  return makeCancellableAPIRequest<
    BaseResponseDto<AnyaConversationMessagesResponse>
  >("GET", `/api/ai/assistant/conversations/${params.conversationId}`, {
    params,
  });
}

export function deleteAnyaConversationMessages(conversationId: string) {
  return makeCancellableAPIRequest(
    "DELETE",
    `/api/ai/assistant/conversations/${conversationId}`,
  );
}

export function sendConfirmation(data: AnyaEventDto) {
  return makeCancellableAPIRequest("POST", "/api/ai/assistant/event", {
    data: {
      event: data.event,
      requestId: data.requestId,
      conversationId: data.conversationId,
    },
  });
}

export function retryRequest(data: AnyaRetryDto) {
  return makeCancellableAPIRequest<AnyaRetryDto>(
    "POST",
    "/api/ai/assistant/retry",
    {
      data,
    },
  );
}

export function cancelRequest(data: AnyaCancelDto) {
  return makeCancellableAPIRequest<AnyaCancelDto>(
    "PUT",
    "/api/ai/assistant/cancel",
    {
      data,
    },
  );
}

export function getAnyaMessage(params: {
  conversationId: string;
  messageId: string;
}) {
  return makeCancellableAPIRequest<BaseResponseDto<any>>(
    "GET",
    `/api/ai/assistant/message/${params.conversationId}/${params.messageId}`,
  );
}

export function storeWidgetData(params: {
  conversationId: string;
  messageId: string;
  widgetId: string;
  apiKey: string;
  data: unknown;
}) {
  return makeCancellableAPIRequest(
    "POST",
    `/api/ai/assistant/store/widget/${params.conversationId}/${params.messageId}`,
    {
      data: {
        widgetId: params.widgetId,
        apiId: params.apiKey,
        data: params.data,
      },
    },
  );
}

/**
 * This should be driven by single backend logic
 * @param props
 * @returns string
 */
export function getConversationIdRequest(props: {
  config?: { title?: string; dashboardId?: string; widgetType?: string };
  dashboardId?: string;
}): CancellableAPIRequest<string | undefined> {
  const historyRequest = getAnyaConversations({});
  const { promise, cancel } = makeCancellablePromise(
    (async () => {
      const response = await historyRequest.promise;
      const history = response.data?.history || [];

      if (history) {
        const normalizedTitle = props?.config?.title || "";
        const dashboardId = props?.dashboardId || props?.config?.dashboardId;
        const widgetType =
          props?.config?.widgetType === undefined
            ? "Findings"
            : props?.config?.widgetType;

        if (!dashboardId) {
          return undefined;
        }

        const hashInput = dashboardId + normalizedTitle + widgetType;
        const currentWidgetId = await sha256Hash(hashInput);
        const conversation = history?.find(
          (convo) => convo.widgetId === currentWidgetId,
        );
        return conversation?.conversationId;
      }
    })(),
  );

  return {
    promise,
    cancel: () => {
      cancel();
      historyRequest.cancel();
    },
  };
}

export async function getConversationId(props: {
  config?: { title?: string; dashboardId?: string; widgetType?: string };
  dashboardId?: string;
}) {
  return getConversationIdRequest(props).promise;
}
